import { Toast } from '../classes/AppToast';

const SILENT_UPDATE_KEY = 'wetag-sw-silent-reload';

let nsw;
let swRegistration = null;
let updateToastShown = false;
let isCheckingUpdate = false;
let isActivating = false;
let initialCheckDone = false;
let lastUpdateSource = 'initial';

async function getRegistration() {
    return swRegistration ?? (await navigator.serviceWorker.getRegistration());
}

/**
 * Activa el SW en waiting (siempre usa reg.waiting actual) y recarga la página.
 */
async function activateWaitingWorker() {
    if (isActivating) {
        return;
    }
    isActivating = true;

    try {
        const reg = await getRegistration();

        if (!reg?.waiting || !navigator.serviceWorker.controller) {
            sessionStorage.removeItem(SILENT_UPDATE_KEY);
            window.location.reload();
            return;
        }

        const waiting = reg.waiting;

        await new Promise((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled) {
                    return;
                }
                settled = true;
                resolve();
            };

            navigator.serviceWorker.addEventListener('controllerchange', finish, { once: true });

            try {
                waiting.postMessage({ type: 'SKIP_WAITING' });
            } catch (err) {
                console.error('SKIP_WAITING postMessage failed', err);
                finish();
                return;
            }

            setTimeout(finish, 3000);
        });

        sessionStorage.removeItem(SILENT_UPDATE_KEY);
        window.location.reload();
    } catch (err) {
        console.error('activateWaitingWorker failed', err);
        isActivating = false;
        window.location.reload();
    }
}

function showUpdateToast() {
    if (updateToastShown) {
        return;
    }
    updateToastShown = true;

    Toast.show('Hay una nueva versión disponible, hacé click acá para actualizar.', 'warning', true, function () {
        activateWaitingWorker();
    });
}

function handleWaitingWorker(source) {
    if (!navigator.serviceWorker.controller) {
        return;
    }

    if (source === 'initial') {
        if (sessionStorage.getItem(SILENT_UPDATE_KEY)) {
            showUpdateToast();
            return;
        }
        sessionStorage.setItem(SILENT_UPDATE_KEY, '1');
        activateWaitingWorker();
    } else {
        showUpdateToast();
    }
}

async function checkForServiceWorkerUpdate(source = 'focus') {
    if (isCheckingUpdate || isActivating) {
        return;
    }
    isCheckingUpdate = true;
    lastUpdateSource = source;

    try {
        const reg = await getRegistration();
        if (!reg) {
            return;
        }

        await reg.update();

        if (!reg.waiting) {
            sessionStorage.removeItem(SILENT_UPDATE_KEY);
        } else if (navigator.serviceWorker.controller) {
            handleWaitingWorker(source);
        }
    } catch (err) {
        console.error('Service Worker update check failed', err);
    } finally {
        isCheckingUpdate = false;
    }
}

function onAppVisible() {
    if (!initialCheckDone) {
        return;
    }
    checkForServiceWorkerUpdate('focus');
}

if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        const swPath = new URL('../ServiceWorker.js', import.meta.url).href;

        navigator.serviceWorker
            .register(swPath, {
                scope: '/',
                updateViaCache: 'none'
            })
            .then((registration) => {
                swRegistration = registration;

                registration.addEventListener('updatefound', () => {
                    const sourceForThisInstall = lastUpdateSource;
                    nsw = registration.installing;
                    if (!nsw) {
                        return;
                    }

                    let handled = false;
                    nsw.addEventListener('statechange', () => {
                        if (
                            handled ||
                            nsw.state !== 'installed' ||
                            !registration.waiting ||
                            !navigator.serviceWorker.controller
                        ) {
                            return;
                        }
                        handled = true;
                        handleWaitingWorker(sourceForThisInstall);
                    });
                });

                return checkForServiceWorkerUpdate('initial');
            })
            .then(() => {
                initialCheckDone = true;
            })
            .catch((err) => console.error('Service Worker Error', err));

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                onAppVisible();
            }
        });

        window.addEventListener('focus', onAppVisible);

        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                onAppVisible();
            }
        });
    });
}
